import { useCallback, useEffect, useState } from 'react'
import { LineChart, ShieldAlert } from 'lucide-react'
import '../components/performance/performance.css'
import {
  useGetPerformance,
  useGetManagers,
  useGetVehicles,
  useGetShiftPerformance,
} from '../hooks/backend/performance'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { PerformanceFilters } from '../components/performance/PerformanceFilters'
import { FilterBar, CollapsibleBlock } from '../components/common/FilterBar'
import { OperatorsView } from '../components/operator/OperatorsView'
import { KpiCards } from '../components/performance/KpiCards'
import { TrendChart } from '../components/performance/TrendChart'
import { EarningsPerShiftChart } from '../components/performance/EarningsPerShiftChart'
import { ManagerBreakdown } from '../components/performance/ManagerBreakdown'
import { DriverTable } from '../components/performance/DriverTable'
import { VehicleTable } from '../components/performance/VehicleTable'
import { ShiftTable } from '../components/performance/ShiftTable'
import {
  type PerformanceData,
  type PerformanceFilters as Filters,
  type ShiftPerformanceData,
} from '../utils/performance'

const EMPTY: PerformanceData = {
  kpis: {
    // Before the figures arrive they are missing, not R 0 (B16).
    uber_gross: null,
    driver_net: null,
    petrol_cost: null,
    petrol_budget: null,
    fuel_saving: null,
    driver_extras: null,
    shifts: 0,
    active_drivers: 0,
    earnings_per_shift: null,
    shifts_excl_cancelled: 0,
    avg_online_hours: null,
    acceptance_rate: null,
    avg_fare: null,
    shifts_with_activity: 0,
  },
  trend: [],
  managers: [],
  drivers: [],
  vehicles: [],
  targets: null,
}

const EMPTY_SHIFTS: ShiftPerformanceData = { rows: [], total: 0 }

const SHIFT_PAGE = 300
const SHIFT_CAP = 1000

const DEFAULT_FILTERS: Filters = {
  from: null,
  to: null,
  manager: 'All',
  vehicle: 'All',
  period: 'month',
}

// Two views under one tab, and one nav entry. Fleet is everything this screen
// already did. Operators is the accountability layer: what each person said they
// own, and what is actually measured against it.
type View = 'fleet' | 'operators'

export default function Performance() {
  const { loading: accessLoading, canView, isAdmin, caption } = useCapabilities()
  const [view, setView] = useState<View>('fleet')
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS)
  const [shiftLimit, setShiftLimit] = useState(SHIFT_PAGE)

  const performance = useGetPerformance()
  const managersFn = useGetManagers()
  const vehiclesFn = useGetVehicles()
  const shiftsFn = useGetShiftPerformance()
  const trigger = performance.trigger
  const triggerManagers = managersFn.trigger
  const triggerVehicles = vehiclesFn.trigger
  const triggerShifts = shiftsFn.trigger

  const load = useCallback(() => {
    trigger(
      {
        from: filters.from,
        to: filters.to,
        manager: filters.manager,
        vehicle: filters.vehicle,
        period: filters.period,
      },
      { skipCache: true },
    )
  }, [
    trigger,
    filters.from,
    filters.to,
    filters.manager,
    filters.vehicle,
    filters.period,
  ])

  useEffect(() => {
    if (canView && view === 'fleet') load()
  }, [canView, view, load])

  useEffect(() => {
    if (canView && view === 'fleet') triggerManagers({})
  }, [canView, view, triggerManagers])

  useEffect(() => {
    if (canView && view === 'fleet') triggerVehicles({})
  }, [canView, view, triggerVehicles])

  // Reset the shift drill-down page whenever the filters (other than the trend
  // period, which the shift table ignores) change.
  useEffect(() => {
    setShiftLimit(SHIFT_PAGE)
  }, [filters.from, filters.to, filters.manager, filters.vehicle])

  // Load shift-level rows, re-fetching when filters or the page limit change.
  useEffect(() => {
    if (!canView || view !== 'fleet') return
    triggerShifts(
      {
        from: filters.from,
        to: filters.to,
        manager: filters.manager,
        vehicle: filters.vehicle,
        limit: shiftLimit,
        sort: 'date_desc',
      },
      { skipCache: true },
    )
  }, [
    canView,
    view,
    triggerShifts,
    filters.from,
    filters.to,
    filters.manager,
    filters.vehicle,
    shiftLimit,
  ])

  const data = (performance.data as PerformanceData | undefined) ?? EMPTY
  const managers = (managersFn.data as string[] | undefined) ?? []
  const vehicles = (vehiclesFn.data as string[] | undefined) ?? []
  const shifts = (shiftsFn.data as ShiftPerformanceData | undefined) ?? EMPTY_SHIFTS
  const canLoadMore = shifts.rows.length < shifts.total && shiftLimit < SHIFT_CAP

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            The Performance view is available to admins only.
          </p>
          <p className="text-xs text-muted-foreground">
            Contact an admin if you need access to fleet financials.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <LineChart className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Performance</h1>
          {view === 'fleet' && performance.loading && (
            <Spinner className="h-4 w-4 text-muted-foreground" />
          )}
          <div className="ml-auto flex items-center gap-1 rounded-md border border-border p-0.5">
            {(['fleet', 'operators'] as View[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`rounded px-3 py-1 text-xs font-medium capitalize transition-colors ${
                  view === v
                    ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
      </div>

      {/* Filters. Fleet only: they filter shifts, which the Operators view does
          not read. Showing them there would imply they do something. */}
      {view === 'fleet' ? (
        <FilterBar
          activeCount={
            (filters.from ? 1 : 0) +
            (filters.to ? 1 : 0) +
            (filters.manager !== 'All' ? 1 : 0) +
            (filters.vehicle !== 'All' ? 1 : 0)
          }
          summary={[
            filters.from || filters.to
              ? `${filters.from || 'start'} to ${filters.to || 'today'}`
              : 'All time',
            filters.manager !== 'All' ? filters.manager : null,
            filters.vehicle !== 'All' ? filters.vehicle : null,
          ]
            .filter(Boolean)
            .join(', ')}
          trailing={`${filters.period} trend`}
        >
          <PerformanceFilters
            filters={filters}
            managers={managers}
            vehicles={vehicles}
            onChange={setFilters}
          />
        </FilterBar>
      ) : null}

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {view === 'operators' ? (
          <OperatorsView isAdmin={isAdmin} />
        ) : performance.error ? (
          <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
            Failed to load performance data: {performance.error}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <KpiCards kpis={data.kpis} targets={data.targets} loading={performance.loading} />
              <p className="text-xs text-muted-foreground">
                Earnings per shift excludes cancelled shifts. All other tiles
                include every shift.
              </p>
            </div>
            {/* Charts and the manager split are context on a phone: they are
                what you look at second, after the number you came for. Each
                folds away on a small screen and is untouched at sm and above. */}
            <CollapsibleBlock title="Trend">
              <TrendChart data={data.trend} period={filters.period} loading={performance.loading} />
            </CollapsibleBlock>
            <CollapsibleBlock title="Earnings per shift">
              <EarningsPerShiftChart data={data.trend} period={filters.period} loading={performance.loading} />
            </CollapsibleBlock>
            <CollapsibleBlock title="By manager">
              <ManagerBreakdown data={data.managers} loading={performance.loading} />
            </CollapsibleBlock>
            <DriverTable rows={data.drivers} targets={data.targets} loading={performance.loading} />
            <VehicleTable rows={data.vehicles} targets={data.targets} loading={performance.loading} />
            <ShiftTable
              rows={shifts.rows}
              total={shifts.total}
              loading={shiftsFn.loading}
              onLoadMore={() => setShiftLimit((n) => Math.min(n + SHIFT_PAGE, SHIFT_CAP))}
              canLoadMore={canLoadMore}
            />
          </div>
        )}
      </div>
    </div>
  )
}
